import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Package, Truck, Award, Zap, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_kpmx-service-portal/artifacts/p1ncs8ut_IMG_4126.jpeg";
const ENGINE_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_kpmx-service-portal/artifacts/qdzrafyv_1000229499.jpeg";
const VINTAGE_IMG = "https://images.unsplash.com/photo-1651980765175-ee248a9d55ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwZGlydCUyMGJpa2V8ZW58MHx8fHwxNzg2ODIxNjU0fDA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Wrench, title: "Engine Rebuilds", desc: "Two-stroke & four-stroke rebuilds done right." },
  { icon: Award, title: "Custom Race Bikes", desc: "VMXDN-ready builds from evo classics to full customs." },
  { icon: Zap, title: "Aftermarket Fitting", desc: "Parts fitted correctly — the way they were designed to run." },
  { icon: ShieldCheck, title: "Bearing Services", desc: "Wheels, head race, swing arm, linkage — all pressed and greased." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="KPMX motocross rider" className="w-full h-full object-cover object-center opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-zinc-950/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-20 grid md:grid-cols-12 gap-8 items-center w-full">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 bg-orange-600/10 border border-orange-600/40 px-3 py-1.5 mb-6">
              <span className="w-2 h-2 bg-orange-500 animate-pulse" />
              <span className="font-mono text-xs uppercase tracking-widest text-orange-400">Motocross Engine Specialists</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-8xl uppercase leading-[0.9] text-white mb-6">
              Rebuilt<br />
              <span className="text-orange-500">To Race.</span><br />
              Restored To Perfection.
            </h1>
            <p className="text-lg text-zinc-300 max-w-xl mb-8">
              KPMX repair and rebuild motocross engines. Restore and rebuild old/Evo motocross
              bikes to the highest level, whether you plan on using them for events such as VMXDN
              or keeping them for your personal collection. We also replace any worn parts on
              your bike such as all the bearings, chain, sprockets, tyres and more. If you want
              peace of mind and to get the most reliability and performance from your new bike
              then book it in with KPMX and we will go over the bike and grease all the chassis
              components and bearings to ensure the bike performs at its best.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="btn-hard inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-8 py-4 font-display uppercase tracking-widest text-sm"
              >
                Book Your Rebuild <ArrowRight size={18} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-orange-500 text-white px-8 py-4 font-display uppercase tracking-widest text-sm transition-colors"
              >
                See Services
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 hidden md:block">
            <div className="border border-white/10 bg-zinc-950/70 backdrop-blur p-6 space-y-4">
              <div className="flex justify-between items-baseline border-b border-white/10 pb-3">
                <span className="font-mono text-xs uppercase text-zinc-500">Engines rebuilt</span>
                <span className="font-display text-3xl text-orange-500">500+</span>
              </div>
              <div className="flex justify-between items-baseline border-b border-white/10 pb-3">
                <span className="font-mono text-xs uppercase text-zinc-500">Years racing</span>
                <span className="font-display text-3xl text-orange-500">12+</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="font-mono text-xs uppercase text-zinc-500">VMXDN builds</span>
                <span className="font-display text-3xl text-orange-500">80+</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection methods */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24" data-testid="collection-methods">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="border border-white/10 bg-zinc-900/50 p-8 md:p-10 hover:border-orange-500/60 transition-colors">
            <Package size={36} className="text-orange-500 mb-6" />
            <h3 className="font-display uppercase text-3xl mb-3">Send Us Your Engine</h3>
            <p className="text-zinc-400 mb-6">
              We ship a purpose-built box to your door. You pack your engine and post it back.
              We rebuild and courier it home to you.
            </p>
            <Link to="/book" className="font-mono text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400 inline-flex items-center gap-2">
              Book courier collection <ArrowRight size={14} />
            </Link>
          </div>
          <div className="border border-white/10 bg-zinc-900/50 p-8 md:p-10 hover:border-orange-500/60 transition-colors">
            <Truck size={36} className="text-orange-500 mb-6" />
            <h3 className="font-display uppercase text-3xl mb-3">We Collect Your Bike</h3>
            <p className="text-zinc-400 mb-6">
              For whole-bike work — bearings, custom builds, or new-bike prep — we come out
              and pick up the machine.
            </p>
            <Link to="/book" className="font-mono text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400 inline-flex items-center gap-2">
              Book bike collection <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-black border-y border-white/10" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-orange-500">— What We Do</span>
              <h2 className="font-display text-4xl sm:text-6xl uppercase mt-2">Built. Broken in. Rebuilt.</h2>
            </div>
            <Link to="/services" className="font-mono text-xs uppercase tracking-widest text-white border-b border-orange-500 pb-1 hover:text-orange-400 w-fit">
              All Services →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {services.map((s) => (
              <div key={s.title} className="bg-zinc-950 p-8 hover:bg-zinc-900 transition-colors">
                <s.icon size={28} className="text-orange-500 mb-6" />
                <h3 className="font-display uppercase text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Split feature */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6 relative">
            <img src={ENGINE_IMG} alt="Engine parts" className="w-full aspect-[4/5] object-cover grayscale hover:grayscale-0 transition-all duration-500" />
            <div className="absolute -bottom-6 -right-6 bg-orange-600 text-white p-6 hidden md:block">
              <span className="font-mono text-xs uppercase tracking-widest">Since</span>
              <div className="font-display text-4xl">2014</div>
            </div>
          </div>
          <div className="md:col-span-6">
            <span className="font-mono text-xs uppercase tracking-widest text-orange-500">— Who We Are</span>
            <h2 className="font-display text-4xl sm:text-6xl uppercase mt-2 mb-6">12 Years<br />Racing Experience.</h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              As a rider we know what needs to be done. We know what other riders go through. We
              have experience on working on bikes daily so we can ensure top quality work and will
              try turn bikes around as fast as we can. Located in the south west of England but
              can take engines from all over the UK via our courier service.
            </p>
            <Link to="/about" className="font-mono text-xs uppercase tracking-widest text-white border-b border-orange-500 pb-1 hover:text-orange-400">
              About KPMX →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-y border-white/10 overflow-hidden">
        <img src={VINTAGE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-8 py-24">
          <h2 className="font-display text-4xl sm:text-6xl uppercase mb-4">Ready For A Rebuild?</h2>
          <p className="text-zinc-300 mb-8 text-lg">Book online in 60 seconds. We handle collection, rebuild, and return.</p>
          <Link to="/book" data-testid="cta-book-btn" className="btn-hard inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-10 py-4 font-display uppercase tracking-widest">
            Start Your Booking <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
